import { useMemo } from 'react';

import { useEventInfo } from '@shared/utils/eventInfo';
import { addSuppressedError } from '@shared/utils/logger';
import { useEventVenueAirportQuery } from './EventVenueAirport.graphql.generated';
import type { CarSearchDestination } from '@shared/utils/accommodations/carSearchParams.types';

type EventCarDefaultLocation = Readonly<{
  loading: boolean;
  /** The location is the couple's saved airport rather than the venue. */
  isAirport: boolean;
  location: Maybe<CarSearchDestination>;
}>;

/** Where car search starts for this event: the couple's saved airport, then the venue. */
export const useEventCarDefaultLocation = (options?: Readonly<{ skip?: boolean }>): EventCarDefaultLocation => {
  const { loading: eventInfoLoading, eventInfo } = useEventInfo();
  const eventId = eventInfo?.eventId;
  const skip = !!options?.skip || !eventId;

  const { data, loading: queryLoading } = useEventVenueAirportQuery({
    skip,
    ssr: false,
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    variables: { eventId: eventId ?? '' },
    // A failed lookup falls back to the venue, so an outage never hides a CTA that shows today.
    onError: error => addSuppressedError(error, { context: 'useEventCarDefaultLocation' }),
  });

  const airport = data?.event?.travelSettings?.venueAirport;
  const venue = eventInfo?.locationInfo;
  // Stable while the cached airport is, so consumers' memos and effects don't rerun every render.
  const location = useMemo(
    () => (airport ? { name: airport.name, placeId: airport.googlePlaceId, latitude: airport.latitude, longitude: airport.longitude } : venue),
    [airport, venue],
  );

  return { location, isAirport: !!airport, loading: !!eventInfoLoading || (!skip && queryLoading) };
};
